import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Unplug } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { selectRbacContext } from "@/rbac/rbacSlice";
import { isSuperAdminContext } from "@/features/ai/hooks/useIsAIEnabled";
import { useSocketListener } from "@/sockets/hooks/useSocketListener";
import { SocketEvents } from "@/sockets/socketEvents";
import { formatDate } from "@/utils/date";
import { setActiveSourceTab } from "@/features/sourceAndCampaign/stores/leadSourceFunctional";
import {
  RECONNECT_REQUIRED_QUERY_KEY,
  useReconnectRequiredSources,
  type ReconnectRequiredSource,
} from "@/features/sourceAndCampaign/api/useReconnectRequiredSources";

/** "Remind me later" hides a source this long (per browser). */
const SNOOZE_MS = 4 * 60 * 60 * 1000;
const SNOOZE_STORAGE_KEY = "xale.sourceReconnectSnooze";
const SOURCES_PATH = "/source-management";

type SnoozeMap = Record<string, number>;

function readSnoozes(): SnoozeMap {
  try {
    const raw = window.localStorage.getItem(SNOOZE_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as SnoozeMap) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSnoozes(map: SnoozeMap) {
  try {
    window.localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage blocked (private mode) — the popup just shows again next load.
  }
}

/**
 * "Reconnect required" popup for a tenant's Super Admins: a lead source
 * (Meta today) stopped syncing in a way only a person can fix — expired
 * login, deleted/blocked app, missing permission, unreadable app secret.
 *
 * Source of truth is the server flag (GET …/lead-source/reconnect-required),
 * so it also shows at login for an admin who was offline when it broke.
 * Live: `source:reconnect_required` (raised, or the daily reminder) re-opens
 * it even if snoozed; `source:reconnect_resolved` closes it.
 * Not shown on the Sources page itself — the badge on the card is there.
 */
export default function SourceReconnectAlertHost() {
  const ctx = useAppSelector(selectRbacContext);
  const isSuperAdmin = isSuperAdminContext(ctx);
  const { sources } = useReconnectRequiredSources(isSuperAdmin);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [snoozes, setSnoozes] = useState<SnoozeMap>(() => readSnoozes());

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: RECONNECT_REQUIRED_QUERY_KEY });
  }, [queryClient]);

  // Raised / daily reminder: un-snooze that source so it pops right away.
  useSocketListener(SocketEvents.SOURCE_RECONNECT_REQUIRED, (payload: { sourceId?: number }) => {
    if (!isSuperAdmin) return;
    const id = Number(payload?.sourceId);
    if (Number.isFinite(id)) {
      setSnoozes((prev) => {
        if (!(String(id) in prev)) return prev;
        const next = { ...prev };
        delete next[String(id)];
        writeSnoozes(next);
        return next;
      });
    }
    refresh();
  });

  // Healthy again — drop it at once, then confirm with the server.
  useSocketListener(SocketEvents.SOURCE_RECONNECT_RESOLVED, (payload: { sourceId?: number }) => {
    if (!isSuperAdmin) return;
    const id = Number(payload?.sourceId);
    queryClient.setQueryData<ReconnectRequiredSource[]>(RECONNECT_REQUIRED_QUERY_KEY, (prev) =>
      Array.isArray(prev) ? prev.filter((s) => s.id !== id) : prev,
    );
    refresh();
  });

  const visible = useMemo(() => {
    const now = Date.now();
    return sources.filter((s) => !(snoozes[String(s.id)] > now));
  }, [sources, snoozes]);

  const snooze = useCallback(
    (ids: number[], ms: number = SNOOZE_MS) => {
      const until = Date.now() + ms;
      setSnoozes((prev) => {
        const next: SnoozeMap = {};
        // Keep only live entries so the map can't grow forever.
        const now = Date.now();
        for (const [k, v] of Object.entries(prev)) if (v > now) next[k] = v;
        for (const id of ids) next[String(id)] = until;
        writeSnoozes(next);
        return next;
      });
    },
    [],
  );

  if (!isSuperAdmin || visible.length === 0) return null;
  if (location.pathname.startsWith(SOURCES_PATH)) return null;

  const single = visible.length === 1 ? visible[0] : null;

  const reconnect = (sourceId: number) => {
    // While they fix it, don't re-pop the moment they leave the Sources page.
    snooze(visible.map((s) => s.id), 60 * 60 * 1000);
    dispatch(setActiveSourceTab("Source Management"));
    navigate(`${SOURCES_PATH}?editSource=${sourceId}`);
  };

  const sourceLabel = (s: ReconnectRequiredSource) =>
    `${s.name || "Lead source"}${s.type ? ` (${s.type === "META" ? "Meta" : s.type})` : ""}`;

  return (
    <Dialog open onOpenChange={(open) => !open && snooze(visible.map((s) => s.id))}>
      <DialogContent className="sm:max-w-[480px] rounded-xl">
        <DialogHeader>
          <p className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span className="flex size-8 items-center justify-center rounded-full bg-[#fdecef]">
              <Unplug className="size-4 text-[var(--color-error)]" />
            </span>
            {single
              ? "Reconnect required — leads are not coming in"
              : `${visible.length} lead sources need reconnecting`}
          </p>
          <Separator />
          <DialogDescription className="pt-2 text-[13px] text-[#505e59]" asChild>
            <div className="flex flex-col gap-3">
              {visible.map((s) => (
                <div key={s.id} className="rounded-lg border border-[#f3d2d8] bg-[#fff8f9] px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[#1e302a] truncate">{sourceLabel(s)}</span>
                    {!single && (
                      <button
                        type="button"
                        onClick={() => reconnect(s.id)}
                        className="shrink-0 text-[12px] font-medium text-[#156548] hover:underline"
                      >
                        Reconnect
                      </button>
                    )}
                  </div>
                  {s.syncFailingSince && (
                    <p className="m-0 mt-0.5 text-[12px] text-[#697571]">
                      Not syncing since {formatDate(s.syncFailingSince)}
                    </p>
                  )}
                  <p className="m-0 mt-1.5 text-[12.5px] leading-relaxed text-[#505e59]">
                    {s.reconnectReason || "This source stopped syncing. Reconnect it to resume incoming leads."}
                  </p>
                </div>
              ))}
              <p className="m-0 text-[12px] text-[#697571]">
                Leads aren't lost yet — once the source is reconnected, the missed period can be
                pulled in again.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => snooze(visible.map((s) => s.id))}>
            Remind me later
          </Button>
          <Button variant="primary" onClick={() => reconnect((single ?? visible[0]).id)}>
            Reconnect now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
